// ============================================================
// CHẤM CÂU TRẢ LỜI NGẮN (TEXT INPUT) CÓ KÝ HIỆU TOÁN HỌC
//
// Đáp án mẫu trong DOCX thường là LaTeX sinh từ Equation (vd "$2 \sqrt{3}$"),
// còn người làm bài gõ Unicode ("2√3"), ASCII ("2sqrt(3)", "2pi") hoặc dùng
// bàn phím ký hiệu (mẫu có ô trống □). Module này đưa cả hai về cùng một dạng
// tuyến tính (canonical) rồi so sánh:
//   1. Khớp chuỗi canonical: √2 = sqrt(2) = \sqrt{2}, 2π = 2pi = 2\pi.
//   2. Nếu là biểu thức tính được thì so thêm theo giá trị:
//      1/2 = 0,5 = \frac{1}{2}, 2/√3 = 2√3/3, 2x/3 = \frac{2x}{3}.
//   3. Tọa độ/ma trận so từng phần tử: (1/2; 1) = (0,5; 1).
//
// Quy ước dạng tuyến tính:
//   √(x) ∛(x) ∜(x) √[n](x)    căn
//   x^(2) x_(1)                số mũ / chỉ số (luôn có ngoặc)
//   (a)/(b)                    phân số
//   [1,2;3,4]                  ma trận (hàng cách nhau ";", cột cách nhau ",")
//   (1;2;3)                    tọa độ / khoảng
//   ∫_(0)^(1)x^(2)dx           tích phân
// ============================================================

// Ô trống trong mẫu của bàn phím ký hiệu (vd "(□)/(□)").
export const ANSWER_PLACEHOLDER = '□';

const SUPERSCRIPT_MAP = {
    '⁰': '0', '¹': '1', '²': '2', '³': '3', '⁴': '4', '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9',
    '⁺': '+', '⁻': '-', '⁽': '(', '⁾': ')', 'ⁿ': 'n',
};
const SUBSCRIPT_MAP = {
    '₀': '0', '₁': '1', '₂': '2', '₃': '3', '₄': '4', '₅': '5', '₆': '6', '₇': '7', '₈': '8', '₉': '9',
    '₊': '+', '₋': '-', '₍': '(', '₎': ')',
    'ₐ': 'a', 'ₑ': 'e', 'ₒ': 'o', 'ₓ': 'x', 'ₕ': 'h', 'ₖ': 'k', 'ₗ': 'l', 'ₘ': 'm', 'ₙ': 'n', 'ₚ': 'p',
    'ₛ': 's', 'ₜ': 't',
};

const buildScriptReplacer = (map, marker) => {
    const runRegex = new RegExp(`[${Object.keys(map).join('')}]+`, 'g');
    return (value) => value.replace(runRegex, (run) => `${marker}(${Array.from(run).map((ch) => map[ch]).join('')})`);
};
// Phai doi chi so truoc NFKC, neu khong "x²" se thanh "x2" va "H₂O" thanh "H2O".
const replaceSuperscripts = buildScriptReplacer(SUPERSCRIPT_MAP, '^');
const replaceSubscripts = buildScriptReplacer(SUBSCRIPT_MAP, '_');

const HTML_ENTITY_MAP = {
    '&nbsp;': ' ', '&lt;': '<', '&gt;': '>', '&amp;': '&', '&quot;': '"', '&apos;': "'", '&#39;': "'",
};

// Ky tu noi bo: khoang trang trong \text{} (khong bi xoa trong che do toan)
// va dau ngan cot ma tran (tranh bi nham voi dau phay thap phan).
const TEXT_SPACE = '\u0001';
const MATRIX_COL = '\u0002';

// Lenh LaTeX doi thang sang ky hieu Unicode/ASCII tuong ung.
const LATEX_SYMBOLS = {
    pi: 'π', Pi: 'π', cdot: '*', times: '*', ast: '*', div: '/', pm: '±', mp: '∓',
    le: '≤', leq: '≤', leqslant: '≤', ge: '≥', geq: '≥', geqslant: '≥',
    ne: '≠', neq: '≠', lt: '<', gt: '>', approx: '≈', equiv: '≡', sim: '~', backsim: '∽', cong: '≅',
    mid: '|', vert: '|', infty: '∞', circ: '°', degree: '°', prime: '′',
    in: '∈', notin: '∉', subset: '⊂', subseteq: '⊆', supset: '⊃', not: '¬',
    emptyset: '∅', varnothing: '∅', cup: '∪', cap: '∩', setminus: '∖', forall: '∀', exists: '∃',
    to: '→', rightarrow: '→', Rightarrow: '⇒', implies: '⇒', leftrightarrow: '↔', Leftrightarrow: '⇔', iff: '⇔',
    rightleftharpoons: '⇌', uparrow: '↑', downarrow: '↓',
    angle: '∠', triangle: '△', perp: '⊥', parallel: '∥', frown: '⌒',
    int: '∫', iint: '∫∫', oint: '∮', sum: '∑', prod: '∏', partial: '∂',
    alpha: 'α', beta: 'β', gamma: 'γ', delta: 'δ', Delta: 'Δ', epsilon: 'ε', varepsilon: 'ε',
    theta: 'θ', lambda: 'λ', mu: 'μ', rho: 'ρ', sigma: 'σ', phi: 'φ', varphi: 'φ',
    omega: 'ω', Omega: 'Ω',
};
const LATEX_SPACES = new Set([',', ';', ':', '!', ' ', 'quad', 'qquad', 'enspace', 'thinspace', 'medspace', 'thickspace']);
const LATEX_IGNORED = new Set([
    'left', 'right', 'middle', 'big', 'Big', 'bigg', 'Bigg', 'bigl', 'bigr', 'Bigl', 'Bigr',
    'biggl', 'biggr', 'Biggl', 'Biggr', 'displaystyle', 'textstyle', 'scriptstyle', 'limits', 'nolimits',
]);
// Nhom giu nguyen khoang trang ben trong (chu thuong, khong phai toan).
const LATEX_TEXT_WRAPPERS = new Set(['text', 'textrm', 'textbf', 'textit', 'mbox']);
const LATEX_MATH_WRAPPERS = new Set([
    'mathrm', 'mathit', 'mathbf', 'mathsf', 'mathtt', 'mathbb', 'mathcal', 'mathnormal',
    'operatorname', 'boldsymbol', 'bm',
]);
const LATEX_FUNCTIONS = new Set([
    'sin', 'cos', 'tan', 'cot', 'ln', 'log', 'lg', 'exp', 'lim', 'max', 'min', 'det',
    'arcsin', 'arccos', 'arctan',
]);
const LATEX_FRACTIONS = new Set(['frac', 'dfrac', 'tfrac', 'cfrac']);
const LATEX_ESCAPED_CHARS = new Set(['{', '}', '%', '_', '&', '#', '|']);
const LATEX_MATRIX_BRACKETS = {
    pmatrix: ['(', ')'], bmatrix: ['[', ']'], vmatrix: ['|', '|'], Vmatrix: ['‖', '‖'],
};

const ATOM_REGEX = /^(?:\d+(?:\.\d+)?|[a-zA-Zα-ωΑ-Ωπ∞])$/;
const SIGNED_ATOM_REGEX = /^-(?:\d+(?:\.\d+)?|[a-zA-Zα-ωΑ-Ωπ∞])$/;
const RADICAL_CHARS = '√∛∜';
const RADICAL_OPERAND_REGEX = /^(?:\d+(?:\.\d+)?|[a-zA-Zα-ωΑ-Ωπ∞])/;
const SCRIPT_OPERAND_REGEX = /^(?:-?\d+(?:\.\d+)?|-?[a-zA-Zα-ωΑ-Ωπ∞]|[√∛∜]\([^()]*\))/;
// Khoang trang canh cac ky tu nay bi bo: "x = 2" -> "x=2", "2 √3" -> "2√3".
const MATH_SPACING_CHARS = '=<>+-*/^±∓≤≥≠≈≡√∛∜()[]{}π_%°∈∉∪∩:;,|∞∫∑∏→↔⇒⇔';
const ANSWER_SEPARATOR = '\u0000';
const ANSWER_SEPARATOR_WORD_REGEX = /(^|\s)(?:hoặc|hoac|or|và|va)(?=\s|$)/gi;
const RELATION_SPLIT_REGEX = /(≤|≥|≠|≈|=|<|>)/;
const STANDALONE_PI_REGEX = /(^|[^a-zA-ZÀ-ỿ])pi(?![a-zA-ZÀ-ỿ])/gi;

const isDigit = (ch) => ch >= '0' && ch <= '9';
const replaceAll = (text, search, replacement) => text.split(search).join(replacement);

// Tim dau dong ngoac tuong ung, bo qua ky tu duoc escape bang "\".
const findClosingBracket = (text, openIndex, open = '(', close = ')') => {
    let depth = 0;
    for (let index = openIndex; index < text.length; index += 1) {
        const ch = text[index];
        if (ch === '\\') {
            index += 1;
        } else if (ch === open) {
            depth += 1;
        } else if (ch === close) {
            depth -= 1;
            if (depth === 0) {
                return index;
            }
        }
    }
    return -1;
};

// Tach theo 1 ky tu phan cach, bo qua phan cach nam trong ngoac.
const splitTopLevel = (text, separator) => {
    const parts = [];
    let depth = 0;
    let current = '';

    for (const ch of text) {
        if ('([{'.includes(ch)) {
            depth += 1;
        } else if (')]}'.includes(ch)) {
            depth = Math.max(0, depth - 1);
        }

        if (depth === 0 && ch === separator) {
            parts.push(current);
            current = '';
            continue;
        }
        current += ch;
    }
    parts.push(current);

    return parts;
};

const isFullyWrapped = (text) => text.startsWith('(') && findClosingBracket(text, 0) === text.length - 1;

const unwrapParens = (text) => {
    let value = text.trim();
    while (isFullyWrapped(value)) {
        value = value.slice(1, -1).trim();
    }
    return value;
};

// Bieu thuc dung duoc lam mau so/co so ma khong can them ngoac.
const isAtomicExpression = (text) => {
    const value = text.trim();
    if (ATOM_REGEX.test(value) || isFullyWrapped(value)) {
        return true;
    }
    const radical = /^[√∛∜](?:\[[^\]]*\])?/.exec(value);
    return Boolean(radical) && isFullyWrapped(value.slice(radical[0].length));
};

const wrapIfNeeded = (text) => (isAtomicExpression(text) ? text.trim() : `(${text.trim()})`);

const hasTopLevelOperator = (text, operators) => {
    let depth = 0;
    for (let index = 0; index < text.length; index += 1) {
        const ch = text[index];
        if (ch === '(' || ch === '[' || ch === '{') {
            depth += 1;
        } else if (ch === ')' || ch === ']' || ch === '}') {
            depth -= 1;
        } else if (depth === 0 && index > 0 && operators.includes(ch)) {
            // index > 0: dau am o dau bieu thuc khong tinh la phep tru.
            return true;
        }
    }
    return false;
};

// O Excel giu rich text dang HTML (cell.h): x<sup>2</sup> -> x^(2).
const decodeHtml = (value) => value
    .replace(/<\s*sup\b[^>]*>([\s\S]*?)<\s*\/\s*sup\s*>/gi, '^($1)')
    .replace(/<\s*sub\b[^>]*>([\s\S]*?)<\s*\/\s*sub\s*>/gi, '_($1)')
    .replace(/<\s*br\s*\/?\s*>/gi, ' ')
    .replace(/<\/?[a-z][^>]*>/gi, '')
    .replace(/&(?:nbsp|lt|gt|amp|quot|apos|#39);/gi, (entity) => HTML_ENTITY_MAP[entity.toLowerCase()]);

// ============================================================
// LATEX -> DẠNG TUYẾN TÍNH
// "\frac{2 \sqrt{3}}{3}" -> "2 √(3)/3", "\sqrt[3]{5}" -> "∛(5)", "30^{\circ}" -> "30°"
// ============================================================
const skipSpaces = (text, index) => {
    let cursor = index;
    while (cursor < text.length && /\s/.test(text[cursor])) {
        cursor += 1;
    }
    return cursor;
};

const readLatexCommand = (source, index) => {
    const letters = /^[a-zA-Z]+/.exec(source.slice(index + 1));
    if (letters) {
        return { name: letters[0], end: index + 1 + letters[0].length };
    }
    return { name: source[index + 1] || '', end: Math.min(index + 2, source.length) };
};

// Doc 1 tham so cua lenh LaTeX: {nhom}, \lenh hoac 1 ky tu.
const readLatexArgument = (source, index) => {
    const start = skipSpaces(source, index);
    if (start >= source.length) {
        return { raw: '', end: start };
    }
    if (source[start] === '{') {
        const close = findClosingBracket(source, start, '{', '}');
        return close === -1
            ? { raw: source.slice(start + 1), end: source.length }
            : { raw: source.slice(start + 1, close), end: close + 1 };
    }
    if (source[start] === '\\') {
        const command = readLatexCommand(source, start);
        return { raw: source.slice(start, command.end), end: command.end };
    }
    return { raw: source[start], end: start + 1 };
};

const buildLinearRoot = (radicand, degree) => {
    const body = `(${radicand})`;
    if (!degree || degree === '2') {
        return `√${body}`;
    }
    if (degree === '3') {
        return `∛${body}`;
    }
    if (degree === '4') {
        return `∜${body}`;
    }
    return `√[${degree}]${body}`;
};

const buildLinearFraction = (numerator, denominator) => {
    // Tu so chi la tich (2√3) thi khong can ngoac vi phep tinh di tu trai sang phai.
    const top = hasTopLevelOperator(numerator, '+-±∓=<>≤≥≠') ? `(${numerator})` : numerator;
    return `${top}/${wrapIfNeeded(denominator)}`;
};

function convertLatex(source) {
    let output = '';
    let index = 0;

    while (index < source.length) {
        const ch = source[index];

        if (ch === '$' || ch === '&') {
            output += ' ';
            index += 1;
            continue;
        }

        if (ch === '{') {
            const close = findClosingBracket(source, index, '{', '}');
            const end = close === -1 ? source.length : close;
            const inner = convertLatex(source.slice(index + 1, end));
            index = close === -1 ? source.length : close + 1;
            // Nhom dung truoc ^/_ phai giu ngoac de khong doi nghia: {x+1}^{2}.
            const nextChar = source[skipSpaces(source, index)];
            output += nextChar === '^' || nextChar === '_' ? wrapIfNeeded(inner) : inner;
            continue;
        }

        if (ch === '}') {
            index += 1;
            continue;
        }

        if (ch === '^' || ch === '_') {
            const argument = readLatexArgument(source, index + 1);
            const inner = convertLatex(argument.raw).trim();
            index = argument.end;
            output += ch === '^' && inner === '°' ? '°' : `${ch}(${inner})`;
            continue;
        }

        if (ch !== '\\') {
            output += ch;
            index += 1;
            continue;
        }

        const { name, end } = readLatexCommand(source, index);
        index = end;

        if (name === '(' || name === ')' || name === '[' || name === ']' || name === '\\') {
            output += ' ';
        } else if (LATEX_ESCAPED_CHARS.has(name)) {
            output += name;
        } else if (LATEX_SPACES.has(name)) {
            output += ' ';
        } else if (LATEX_IGNORED.has(name)) {
            // "\left." / "\right." la delimiter rong.
            if (source[index] === '.') {
                index += 1;
            }
        } else if (LATEX_FRACTIONS.has(name)) {
            const numerator = readLatexArgument(source, index);
            const denominator = readLatexArgument(source, numerator.end);
            index = denominator.end;
            const fraction = buildLinearFraction(
                convertLatex(numerator.raw).trim(),
                convertLatex(denominator.raw).trim()
            );
            // Tranh dinh so: "2\frac{1}{3}" khong duoc thanh "21/3".
            output += /\d\s*$/.test(output) ? `(${fraction})` : fraction;
        } else if (name === 'sqrt') {
            let degree = '';
            const cursor = skipSpaces(source, index);
            if (source[cursor] === '[') {
                const close = findClosingBracket(source, cursor, '[', ']');
                degree = convertLatex(source.slice(cursor + 1, close === -1 ? source.length : close)).trim();
                index = close === -1 ? source.length : close + 1;
            }
            const radicand = readLatexArgument(source, index);
            index = radicand.end;
            output += buildLinearRoot(convertLatex(radicand.raw).trim(), degree);
        } else if (name === 'begin') {
            const environment = readLatexArgument(source, index);
            const environmentName = environment.raw.trim();
            let bodyStart = environment.end;
            if (environmentName === 'array') {
                // Bo dac ta cot "{cc}" cua array.
                bodyStart = readLatexArgument(source, bodyStart).end;
            }
            const endTag = `\\end{${environmentName}}`;
            const endIndex = source.indexOf(endTag, bodyStart);
            const body = source.slice(bodyStart, endIndex === -1 ? source.length : endIndex);
            index = endIndex === -1 ? source.length : endIndex + endTag.length;

            // He phuong trinh "\left\{ \begin{matrix} ... \right." -> danh sach nghiem.
            const isSystem = environmentName === 'cases' || /\{\s*$/.test(output);
            if (isSystem) {
                output = output.replace(/\{\s*$/, '');
            }
            output += convertLatexEnvironment(environmentName, body, isSystem);
        } else if (LATEX_TEXT_WRAPPERS.has(name)) {
            const argument = readLatexArgument(source, index);
            index = argument.end;
            output += convertLatex(argument.raw).replace(/\s/g, TEXT_SPACE);
        } else if (LATEX_MATH_WRAPPERS.has(name)) {
            const argument = readLatexArgument(source, index);
            index = argument.end;
            output += convertLatex(argument.raw);
        } else if (Object.prototype.hasOwnProperty.call(LATEX_SYMBOLS, name)) {
            output += LATEX_SYMBOLS[name];
        } else if (LATEX_FUNCTIONS.has(name)) {
            output += name;
        } else {
            // Lenh chua ho tro: giu nguyen de hai ve van so khop chuoi duoc voi nhau.
            output += `\\${name}`;
        }
    }

    return output;
}

// Ma tran: hang -> ";", cot -> MATRIX_COL. He phuong trinh: moi hang la 1 nghiem.
function convertLatexEnvironment(environmentName, body, isSystem) {
    const rows = body
        .split(/\\\\/)
        .map((row) => row.trim())
        .filter(Boolean)
        .map((row) => row
            .split('&')
            .map((cell) => convertLatex(cell).trim())
            .join(isSystem ? '' : MATRIX_COL));
    const content = rows.join(';');

    if (isSystem) {
        return content;
    }

    const brackets = LATEX_MATRIX_BRACKETS[environmentName];
    return brackets ? `${brackets[0]}${content}${brackets[1]}` : content;
}

// Buoc chuan hoa chung cho ca chuoi, chay TRUOC khi tach thanh tung nghiem.
const preNormalizeAnswer = (raw) => {
    let text = replaceAll(decodeHtml(String(raw ?? '')), ANSWER_PLACEHOLDER, '');
    text = replaceSubscripts(replaceSuperscripts(text)).normalize('NFKC');

    if (/[\\$]/.test(text)) {
        // Trong $...$ khoang trang khong co nghia ("x = - 2", "d x"), bo het.
        text = text.replace(
            /\$\$([\s\S]+?)\$\$|\$([\s\S]+?)\$/g,
            (match, block, inline) => ` ${convertLatex(block ?? inline).replace(/\s+/g, '')} `
        );
        if (text.includes('\\')) {
            text = convertLatex(text);
        }
    }

    text = replaceAll(text, TEXT_SPACE, ' ')
        .replace(/ /g, ' ')
        .replace(/[−–—]/g, '-')
        .replace(/⁄/g, '/')
        .replace(/\*\*/g, '^')
        .replace(/[×·⋅∙]/g, '*')
        .replace(/÷/g, '/')
        .replace(/∶/g, ':')
        .replace(/<=/g, '≤')
        .replace(/>=/g, '≥')
        .replace(/!=|=\/=|<>/g, '≠')
        .replace(/\+\s*\/?\s*-/g, '±');

    // Dau phay thap phan kieu Viet Nam: "0,5" -> "0.5". Chuoi "1,2,3" van la danh sach.
    return text.replace(/\d+(?:,\d+)+/g, (match) => (match.split(',').length === 2 ? match.replace(',', '.') : match));
};

// Bo ngoac nhon le o 2 dau (con sot lai tu he phuong trinh "\left\{ ... \right.").
const trimUnbalancedBraces = (text) => {
    let value = text.trim();
    if (value.startsWith('{') && findClosingBracket(value, 0, '{', '}') === -1) {
        value = value.slice(1).trim();
    }
    const opens = (value.match(/\{/g) || []).length;
    const closes = (value.match(/\}/g) || []).length;
    if (value.endsWith('}') && closes > opens) {
        value = value.slice(0, -1).trim();
    }
    return value;
};

// Tach cac nghiem theo ; , "hoặc" "và" "or" — bo qua dau phan cach nam trong ngoac
// de khong cat vo toa do "(1;2;3)", khoang "(1;3)" hay ma tran "[1 2; 3 4]".
const splitAnswerParts = (text) => {
    let source = trimUnbalancedBraces(text);
    // Tap hop "{1; 2}" duoc xem nhu danh sach nghiem.
    if (source.startsWith('{') && findClosingBracket(source, 0, '{', '}') === source.length - 1) {
        source = source.slice(1, -1);
    }

    const marked = source.replace(ANSWER_SEPARATOR_WORD_REGEX, `$1${ANSWER_SEPARATOR}`);
    return splitTopLevel(marked, ';')
        .flatMap((part) => splitTopLevel(part, ','))
        .flatMap((part) => part.split(ANSWER_SEPARATOR));
};

const collapseSpaces = (text) => text.replace(/(\S)\s+(?=(\S))/g, (match, before, after) => {
    // Giu khoang trang giua 2 so ("1 2") va giua 2 tu ("hà nội").
    if (isDigit(before) && isDigit(after)) {
        return `${before} `;
    }
    if (isDigit(before) || isDigit(after) || MATH_SPACING_CHARS.includes(before) || MATH_SPACING_CHARS.includes(after)) {
        return before;
    }
    return `${before} `;
});

// Moi dau can deu co doi so trong ngoac: √2, √(2), √((2)) -> √(2).
const normalizeRadicals = (text) => {
    let output = '';
    let index = 0;

    while (index < text.length) {
        const ch = text[index];
        output += ch;
        index += 1;

        if (!RADICAL_CHARS.includes(ch)) {
            continue;
        }

        if (ch === '√' && text[index] === '[') {
            const close = findClosingBracket(text, index, '[', ']');
            if (close === -1) {
                continue;
            }
            output += text.slice(index, close + 1);
            index = close + 1;
        }

        if (text[index] === '(') {
            const close = findClosingBracket(text, index);
            if (close === -1) {
                continue;
            }
            output += `(${normalizeRadicals(unwrapParens(text.slice(index + 1, close)))})`;
            index = close + 1;
            continue;
        }

        const operand = RADICAL_OPERAND_REGEX.exec(text.slice(index));
        if (operand) {
            output += `(${operand[0]})`;
            index += operand[0].length;
        }
    }

    return output;
};

// So mu/chi so luon co ngoac: x^2 -> x^(2), x^-1 -> x^(-1), a_n -> a_(n).
const normalizeScripts = (text) => {
    let output = '';
    let index = 0;

    while (index < text.length) {
        const ch = text[index];
        output += ch;
        index += 1;

        if ((ch !== '^' && ch !== '_') || text[index] === '(') {
            continue;
        }

        const operand = SCRIPT_OPERAND_REGEX.exec(text.slice(index));
        if (operand) {
            output += `(${operand[0]})`;
            index += operand[0].length;
        }
    }

    return output;
};

// "2*√3" -> "2√3", "2*x" -> "2x"; giu "2*3" va "2*-3" de khong doi gia tri.
const removeImplicitMultiplicationStars = (text) => text.replace(/\*/g, (match, offset, source) => {
    const before = source[offset - 1] || '';
    const after = source[offset + 1] || '';
    return before && /[a-zA-Zα-ωΑ-Ωπ√∛∜(]/.test(after) ? '' : match;
});

// Bo ngoac thua quanh 1 so/1 bien: (1)/(2) -> 1/2, (x)^(2) -> x^(2).
const stripRedundantParens = (text) => {
    let current = text;

    for (let pass = 0; pass < 10; pass += 1) {
        const next = current.replace(/\(([^()]*)\)/g, (match, inner, offset, source) => {
            const before = source[offset - 1] || '';
            const after = source[offset + match.length] || '';
            const isSigned = SIGNED_ATOM_REGEX.test(inner);

            if (!ATOM_REGEX.test(inner) && !isSigned) {
                return match;
            }
            // Doi so cua dau can, ham so (sin(x)), so mu/chi so luon giu ngoac.
            if (before && /[√∛∜\]a-zA-Z^_]/.test(before)) {
                return match;
            }
            // Khong de 2 so dinh vao nhau: 2(3) khong duoc thanh 23.
            if ((/[\d.]/.test(before) && /^[\d.]/.test(inner)) || (/[\d.]/.test(after) && /[\d.]$/.test(inner))) {
                return match;
            }
            // (-2)^2 khac -2^2; 3(-2) khac 3-2.
            if (isSigned && (after === '^' || (before && !/[(=<>≤≥≠≈;,]/.test(before)))) {
                return match;
            }
            return inner;
        });

        if (next === current) {
            break;
        }
        current = next;
    }

    return current;
};

// Ma tran go tay "[1 -2; 3 4]" hoac tu LaTeX: chuan hoa tung o roi ghep "[1,-2;3,4]".
const canonicalizeMatrices = (text, caseSensitive) => {
    let output = '';
    let index = 0;

    while (index < text.length) {
        const ch = text[index];
        const close = ch === '[' && text[index - 1] !== '√' ? findClosingBracket(text, index, '[', ']') : -1;
        if (close === -1) {
            output += ch;
            index += 1;
            continue;
        }

        const inner = text.slice(index + 1, close);
        if (!inner.includes(';') && !inner.includes(MATRIX_COL)) {
            output += ch;
            index += 1;
            continue;
        }

        const rows = splitTopLevel(inner, ';').map((row) => {
            const cells = row.includes(MATRIX_COL) ? row.split(MATRIX_COL) : row.trim().split(/\s*,\s+|\s+/);
            return cells.map((cell) => finalizeAnswerToken(cell, caseSensitive)).join(MATRIX_COL);
        });
        output += `[${rows.join(';')}]`;
        index = close + 1;
    }

    return output;
};

function finalizeAnswerToken(part, caseSensitive) {
    let token = String(part || '').replace(/\s+/g, ' ').trim();
    if (!caseSensitive) {
        // Parser da lowercase dap an mau, nen "\Rightarrow" thanh "\rightarrow":
        // gop ⇒/→ va ⇔/↔ de hai ve van khop.
        token = token.toLowerCase().replace(/⇒/g, '→').replace(/⇔/g, '↔');
    }

    token = token
        .replace(/\bsqrt\s*(?=[(\d])/gi, '√')
        .replace(/\bcbrt\s*(?=[(\d])/gi, '∛')
        .replace(STANDALONE_PI_REGEX, '$1π')
        .replace(/^[,;:]+/, '')
        .replace(/[.,;:!?]+$/, '');

    token = canonicalizeMatrices(token, caseSensitive);
    token = collapseSpaces(token);
    token = normalizeRadicals(token);
    token = normalizeScripts(token);
    token = removeImplicitMultiplicationStars(token);

    // "(x=2)" -> "x=2", nhung giu toa do "(1;3)".
    if (isFullyWrapped(token) && !/[;,]/.test(token) && !token.includes(MATRIX_COL)) {
        token = unwrapParens(token);
    }

    return replaceAll(stripRedundantParens(token), MATRIX_COL, ',');
}

// "x=±2" -> ["x=2", "x=-2"] de so voi dap an viet tach 2 nghiem.
const expandPlusMinus = (token) => {
    const signs = token.match(/[±∓]/g);
    if (!signs || signs.length !== 1) {
        return [token];
    }
    const dropLeadingPlus = (value) => value.replace(/(^|[=<>≤≥≠≈(])\+/g, '$1');
    return [dropLeadingPlus(token.replace(/[±∓]/, '+')), token.replace(/[±∓]/, '-')];
};

// ============================================================
// TÍNH GIÁ TRỊ BIỂU THỨC (số, π, √ ∛ ∜, ^, phân số, biến 1-2 chữ cái)
// ============================================================
const EXPRESSION_FUNCTIONS = {
    sqrt: Math.sqrt,
    cbrt: Math.cbrt,
    sin: Math.sin,
    cos: Math.cos,
    tan: Math.tan,
    cot: (value) => 1 / Math.tan(value),
    exp: Math.exp,
    log: Math.log10,
    lg: Math.log10,
    ln: Math.log,
};
const FUNCTION_NAMES = Object.keys(EXPRESSION_FUNCTIONS).sort((a, b) => b.length - a.length);
const findFunctionPrefix = (word) => FUNCTION_NAMES.find((name) => word.startsWith(name));

const nthRoot = (value, degree) => {
    if (value < 0 && Number.isInteger(degree) && Math.abs(degree % 2) === 1) {
        return -((-value) ** (1 / degree));
    }
    return value ** (1 / degree);
};

const tokenizeExpression = (text) => {
    const tokens = [];
    let index = 0;

    while (index < text.length) {
        const ch = text[index];

        if (ch === ' ') {
            index += 1;
            continue;
        }

        const numberMatch = /^(?:\d+(?:\.\d*)?|\.\d+)/.exec(text.slice(index));
        if (numberMatch) {
            tokens.push({ type: 'number', value: Number(numberMatch[0]) });
            index += numberMatch[0].length;
            continue;
        }

        if (ch === 'π') {
            tokens.push({ type: 'number', value: Math.PI });
            index += 1;
            continue;
        }

        if (/[a-zA-Z]/.test(ch)) {
            let rest = /^[a-zA-Z]+/.exec(text.slice(index))[0];
            index += rest.length;
            let plainLetters = 0;

            while (rest) {
                const functionName = findFunctionPrefix(rest);
                if (functionName) {
                    tokens.push({ type: 'function', name: functionName });
                    rest = rest.slice(functionName.length);
                    plainLetters = 0;
                    continue;
                }
                plainLetters += 1;
                // Tu 3 chu cai lien nhau tro len la tu ngu, khong phai tich cac bien.
                if (plainLetters >= 3) {
                    return null;
                }
                tokens.push({ type: 'variable', name: rest[0] });
                rest = rest.slice(1);
            }
            continue;
        }

        if (/[α-ωΑ-Ω]/.test(ch)) {
            tokens.push({ type: 'variable', name: ch });
            index += 1;
            continue;
        }

        if ('+-*/^()[]√∛∜'.includes(ch)) {
            tokens.push({ type: 'operator', value: ch });
            index += 1;
            continue;
        }

        // Ky hieu khong tinh duoc (%, °, ±, _, ∫...) -> chi so khop chuoi.
        return null;
    }

    return tokens;
};

// Parser de quy xuong: expression > term (* / va nhan ngam) > unary > power > primary.
const evaluateTokens = (tokens, variables) => {
    let position = 0;

    const isOperator = (token, value) => Boolean(token) && token.type === 'operator' && token.value === value;
    const startsPrimary = (token) => Boolean(token) && (token.type !== 'operator' || '(√∛∜'.includes(token.value));
    const expect = (value) => {
        if (!isOperator(tokens[position], value)) {
            throw new Error(`Expected ${value}`);
        }
        position += 1;
    };

    function parsePrimary() {
        const token = tokens[position];
        if (!token) {
            throw new Error('Unexpected end');
        }
        position += 1;

        if (token.type === 'number') {
            return token.value;
        }
        if (token.type === 'variable') {
            return variables[token.name];
        }
        if (token.type === 'function') {
            return EXPRESSION_FUNCTIONS[token.name](parsePower());
        }
        if (token.value === '(') {
            const value = parseExpression();
            expect(')');
            return value;
        }
        if (token.value === '√') {
            if (isOperator(tokens[position], '[')) {
                position += 1;
                const degree = parseExpression();
                expect(']');
                return nthRoot(parsePrimary(), degree);
            }
            return Math.sqrt(parsePrimary());
        }
        if (token.value === '∛') {
            return Math.cbrt(parsePrimary());
        }
        if (token.value === '∜') {
            return nthRoot(parsePrimary(), 4);
        }
        throw new Error(`Unexpected ${token.value}`);
    }

    // Luy thua ket hop phai va manh hon dau am ben trai: -2^2 = -4, 2^-1 = 0.5.
    function parsePower() {
        const base = parsePrimary();
        if (isOperator(tokens[position], '^')) {
            position += 1;
            return base ** parseUnary();
        }
        return base;
    }

    function parseUnary() {
        if (isOperator(tokens[position], '-')) {
            position += 1;
            return -parseUnary();
        }
        if (isOperator(tokens[position], '+')) {
            position += 1;
            return parseUnary();
        }
        return parsePower();
    }

    // Nhan ngam (2√3, 2x, 2π) cung muc uu tien voi * /, tinh tu trai sang phai.
    function parseTerm() {
        let value = parseUnary();
        for (;;) {
            const token = tokens[position];
            if (isOperator(token, '*') || isOperator(token, '/')) {
                position += 1;
                const right = parseUnary();
                value = token.value === '*' ? value * right : value / right;
            } else if (startsPrimary(token)) {
                value *= parsePower();
            } else {
                return value;
            }
        }
    }

    function parseExpression() {
        let value = parseTerm();
        while (isOperator(tokens[position], '+') || isOperator(tokens[position], '-')) {
            const operator = tokens[position].value;
            position += 1;
            const right = parseTerm();
            value = operator === '+' ? value + right : value - right;
        }
        return value;
    }

    const result = parseExpression();
    if (position !== tokens.length) {
        throw new Error('Unexpected trailing tokens');
    }
    return result;
};

// Gia tri thu cho bien: so vo ty de tranh trung hop ngau nhien.
const VARIABLE_SAMPLES = [0.7548776662, 1.3247179572, 2.1180339887];
const VARIABLE_OFFSET = 0.6180339887;
const MATH_LIKE_REGEX = /[\d√∛∜π^*/+\-()]/;

const areNumbersClose = (a, b) => Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));

const collectVariableNames = (tokenLists) => Array.from(new Set(
    tokenLists.flat().filter((token) => token.type === 'variable').map((token) => token.name)
)).sort();

const areExpressionsEquivalent = (left, right) => {
    if (left === right) {
        return true;
    }
    if (!MATH_LIKE_REGEX.test(left) || !MATH_LIKE_REGEX.test(right)) {
        return false;
    }

    const leftTokens = tokenizeExpression(left);
    const rightTokens = tokenizeExpression(right);
    if (!leftTokens || !rightTokens || leftTokens.length === 0 || rightTokens.length === 0) {
        return false;
    }

    const variableNames = collectVariableNames([leftTokens, rightTokens]);
    const trialCount = variableNames.length === 0 ? 1 : VARIABLE_SAMPLES.length;
    let comparedTrials = 0;

    for (let trial = 0; trial < trialCount; trial += 1) {
        const variables = {};
        variableNames.forEach((name, variableIndex) => {
            variables[name] = VARIABLE_SAMPLES[trial] + variableIndex * VARIABLE_OFFSET;
        });

        let leftValue;
        let rightValue;
        try {
            leftValue = evaluateTokens(leftTokens, variables);
            rightValue = evaluateTokens(rightTokens, variables);
        } catch (error) {
            return false;
        }

        const leftFinite = Number.isFinite(leftValue);
        const rightFinite = Number.isFinite(rightValue);
        if (!leftFinite && !rightFinite) {
            continue;
        }
        if (leftFinite !== rightFinite || !areNumbersClose(leftValue, rightValue)) {
            return false;
        }
        comparedTrials += 1;
    }

    return comparedTrials > 0;
};

const isEvaluableToken = (token) => token.split(RELATION_SPLIT_REGEX)
    .filter((piece, index) => index % 2 === 0 && piece !== '')
    .every((piece) => {
        const tokens = tokenizeExpression(piece);
        if (!tokens || tokens.length === 0) {
            return false;
        }
        const variables = {};
        collectVariableNames([tokens]).forEach((name) => {
            variables[name] = 1;
        });
        try {
            evaluateTokens(tokens, variables);
            return true;
        } catch (error) {
            return false;
        }
    });

// Ky hieu hoa hoc khong tinh duoc gia tri: "fe^(3+)" ~ "fe3+", "h_(2)o" ~ "h2o".
// Bieu thuc toan tinh duoc (x^(2)) van phai khop chat de "x2" khong duoc tinh dung.
const areLooselyEquivalent = (userToken, correctToken) => {
    const tokensWithScripts = [userToken, correctToken].filter((token) => /[\^_]/.test(token));
    if (tokensWithScripts.length === 0 || tokensWithScripts.some(isEvaluableToken)) {
        return false;
    }
    const toLooseForm = (token) => token.replace(/[\^_()]/g, '');
    return toLooseForm(userToken) === toLooseForm(correctToken);
};

// Toa do "(1;2;3)", khoang "[1;3)" va ma tran "[1,2;3,4]" -> so tung phan tu.
const getBracketedParts = (token) => {
    if (token.length < 2 || !'(['.includes(token[0]) || !')]'.includes(token[token.length - 1])) {
        return null;
    }

    const inner = token.slice(1, -1);
    let depth = 0;
    for (const ch of inner) {
        if ('([{'.includes(ch)) {
            depth += 1;
        } else if (')]}'.includes(ch)) {
            depth -= 1;
            if (depth < 0) {
                return null;
            }
        }
    }
    if (depth !== 0) {
        return null;
    }

    const rows = splitTopLevel(inner, ';').map((row) => splitTopLevel(row, ','));
    if (rows.length === 1 && rows[0].length === 1) {
        return null;
    }
    return { open: token[0], close: token[token.length - 1], rows };
};

const compareBracketedTokens = (userToken, correctToken) => {
    const user = getBracketedParts(userToken);
    const correct = getBracketedParts(correctToken);
    if (!user || !correct) {
        return null;
    }
    if (user.open !== correct.open || user.close !== correct.close || user.rows.length !== correct.rows.length) {
        return false;
    }
    return correct.rows.every((row, rowIndex) => (
        row.length === user.rows[rowIndex].length
        && row.every((cell, cellIndex) => areAnswerTokensEquivalent(user.rows[rowIndex][cellIndex], cell))
    ));
};

// So 2 nghiem: "x=1/2" va "x=0.5" -> cung dau quan he, tung ve tuong duong.
function areAnswerTokensEquivalent(userToken, correctToken) {
    if (userToken === correctToken) {
        return true;
    }

    const bracketResult = compareBracketedTokens(userToken, correctToken);
    if (bracketResult !== null) {
        return bracketResult;
    }

    const userPieces = userToken.split(RELATION_SPLIT_REGEX);
    const correctPieces = correctToken.split(RELATION_SPLIT_REGEX);
    const piecesMatch = userPieces.length === correctPieces.length && userPieces.every((piece, index) => (
        index % 2 === 1
            ? piece === correctPieces[index]
            : areExpressionsEquivalent(piece, correctPieces[index])
    ));

    return piecesMatch || areLooselyEquivalent(userToken, correctToken);
}

// ============================================================
// CHẤM THEO THUẬT TOÁN SO SÁNH + TRỌNG SỐ
//
// Mỗi cặp (nghiệm người dùng, nghiệm đúng) được chấm điểm 0..1:
//   - Tương đương chuỗi chuẩn hoá / tương đương toán học  -> 1
//   - Biểu thức toán hoặc số mà không tương đương         -> 0 (không "gần đúng")
//   - Câu chữ: trung bình có trọng số của 3 độ giống
//       accentless (0.45): giống ký tự khi bỏ dấu      -> chịu được lỗi gõ
//       accented   (0.40): giống ký tự khi giữ dấu     -> phạt sai/thiếu dấu
//       words      (0.15): tỉ lệ từ trùng (Dice)       -> chịu được đảo từ
// Cặp đạt ngưỡng (mặc định 0.85) mới tính là khớp. Điểm cả câu = tổng điểm
// các cặp ghép được / số nghiệm, nên thiếu hoặc thừa nghiệm đều bị trừ.
//   "ha noi"  ~ "hà nội" = 0.87 -> đúng      "ba" ~ "bà"      = 0.80 -> sai
//   "hà nộii" ~ "hà nội" = 0.88 -> đúng      "x2" ~ "x^(2)"   = 0    -> sai
// ============================================================
export const DEFAULT_SCORING_OPTIONS = {
    // Diem toi thieu de 1 cap nghiem duoc tinh la khop (1 = chi chap nhan khop tuyet doi).
    threshold: 0.85,
    weights: { accentless: 0.45, accented: 0.4, words: 0.15 },
    // Hai tu giong nhau tu muc nay tro len thi tinh la trung (vd "noii" ~ "noi").
    wordMatchThreshold: 0.75,
    // Tat de chi chap nhan tuong duong tuyet doi (hanh vi truoc day).
    fuzzy: true,
};

// Nghiem chua ky hieu toan hoac chi la 1 con so: khong cham "gan dung".
const MATH_TOKEN_REGEX = /[=<>≤≥≠≈≡√∛∜π^_±∓*/+()[\]{}∫∑∏|°%∈∉⊂⊆⊃∪∩→↔∞]|^-?\d+(?:\.\d+)?$/;

const stripVietnameseAccents = (text) => text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');

const levenshteinDistance = (a, b) => {
    const source = Array.from(a);
    const target = Array.from(b);
    if (source.length === 0 || target.length === 0) {
        return Math.max(source.length, target.length);
    }

    let previous = Array.from({ length: target.length + 1 }, (_, index) => index);
    for (let i = 1; i <= source.length; i += 1) {
        const current = [i];
        for (let j = 1; j <= target.length; j += 1) {
            const cost = source[i - 1] === target[j - 1] ? 0 : 1;
            current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
        }
        previous = current;
    }
    return previous[target.length];
};

// 1 - (so phep sua toi thieu / do dai chuoi dai hon).
const characterSimilarity = (a, b) => {
    const maxLength = Math.max(Array.from(a).length, Array.from(b).length);
    return maxLength === 0 ? 1 : 1 - levenshteinDistance(a, b) / maxLength;
};

// Ti le tu trung (Dice); tu gan giong (sai 1 ky tu) van tinh la trung.
const wordSimilarity = (a, b, wordMatchThreshold) => {
    const userWords = a.split(' ').filter(Boolean);
    const correctWords = b.split(' ').filter(Boolean);
    if (userWords.length === 0 || correctWords.length === 0) {
        return 0;
    }

    const used = new Array(userWords.length).fill(false);
    let matched = 0;
    correctWords.forEach((word) => {
        const index = userWords.findIndex((candidate, candidateIndex) => (
            !used[candidateIndex] && characterSimilarity(candidate, word) >= wordMatchThreshold
        ));
        if (index !== -1) {
            used[index] = true;
            matched += 1;
        }
    });

    return (2 * matched) / (userWords.length + correctWords.length);
};

const extractNumbers = (text) => text.match(/\d+(?:\.\d+)?/g) || [];

const scoreTokenPair = (userToken, correctToken, settings) => {
    if (areAnswerTokensEquivalent(userToken, correctToken)) {
        return 1;
    }
    if (!settings.fuzzy || MATH_TOKEN_REGEX.test(userToken) || MATH_TOKEN_REGEX.test(correctToken)) {
        return 0;
    }
    // So nam trong cau chu ("thế kỷ 20") van phai dung tuyet doi.
    if (extractNumbers(userToken).join('|') !== extractNumbers(correctToken).join('|')) {
        return 0;
    }

    const { weights } = settings;
    const totalWeight = weights.accentless + weights.accented + weights.words;
    if (totalWeight <= 0) {
        return 0;
    }

    const userPlain = stripVietnameseAccents(userToken);
    const correctPlain = stripVietnameseAccents(correctToken);
    return (
        weights.accentless * characterSimilarity(userPlain, correctPlain)
        + weights.accented * characterSimilarity(userToken, correctToken)
        + weights.words * wordSimilarity(userPlain, correctPlain, settings.wordMatchThreshold)
    ) / totalWeight;
};

// Ghep nghiem nguoi dung voi nghiem dung theo diem cao nhat truoc (moi nghiem
// chi ghep 1 lan), roi tinh diem ca cau.
//   - 'set': phai du va khong thua -> chia cho so nghiem nhieu hon cua 2 ben.
//   - 'contains': chi can chua du nghiem dung -> chia cho so nghiem dung.
const scoreTokenSets = (userTokens, correctTokens, settings, matchMode) => {
    const candidates = [];
    correctTokens.forEach((correctToken, correctIndex) => {
        userTokens.forEach((userToken, userIndex) => {
            const score = scoreTokenPair(userToken, correctToken, settings);
            if (score > 0) {
                candidates.push({ correctIndex, userIndex, score });
            }
        });
    });
    candidates.sort((a, b) => b.score - a.score);

    const usedCorrect = new Set();
    const usedUser = new Set();
    const matches = [];
    candidates.forEach(({ correctIndex, userIndex, score }) => {
        if (usedCorrect.has(correctIndex) || usedUser.has(userIndex)) {
            return;
        }
        usedCorrect.add(correctIndex);
        usedUser.add(userIndex);
        matches.push({ correctIndex, userIndex, user: userTokens[userIndex], correct: correctTokens[correctIndex], score });
    });

    const passed = matches.filter((match) => match.score >= settings.threshold);
    const denominator = matchMode === 'contains'
        ? correctTokens.length
        : Math.max(userTokens.length, correctTokens.length);
    const totalScore = matches.reduce((sum, match) => sum + match.score, 0);
    const allCorrectPassed = correctTokens.length > 0 && passed.length === correctTokens.length;

    return {
        isCorrect: allCorrectPassed && (matchMode === 'contains' || userTokens.length === correctTokens.length),
        score: denominator === 0 ? 0 : totalScore / denominator,
        matches: matches.map(({ user, correct, score }) => ({ user, correct, score })),
        missing: correctTokens.filter((_, index) => !passed.some((match) => match.correctIndex === index)),
        extra: userTokens.filter((_, index) => !passed.some((match) => match.userIndex === index)),
    };
};

const scoreAgainstAnswer = (userAnswer, acceptedAnswer, config, settings) => {
    const caseSensitive = Boolean(config.caseSensitive);

    if (config.exactMatch) {
        const user = normalizeShortAnswer(userAnswer, caseSensitive);
        const correct = normalizeShortAnswer(acceptedAnswer, caseSensitive);
        const isCorrect = user === correct;
        return {
            isCorrect,
            score: isCorrect ? 1 : 0,
            matches: isCorrect ? [{ user, correct, score: 1 }] : [],
            missing: isCorrect ? [] : [correct],
            extra: isCorrect ? [] : [user],
        };
    }

    if (settings.matchMode === 'contains') {
        const user = normalizeShortAnswer(userAnswer, caseSensitive);
        const correct = normalizeShortAnswer(acceptedAnswer, caseSensitive);
        if (correct && user.includes(correct)) {
            return { isCorrect: true, score: 1, matches: [{ user, correct, score: 1 }], missing: [], extra: [] };
        }
    }

    return scoreTokenSets(
        tokenizeShortAnswer(userAnswer, caseSensitive),
        tokenizeShortAnswer(acceptedAnswer, caseSensitive),
        settings,
        settings.matchMode
    );
};

// ============================================================
// API
// ============================================================

// Chuan hoa ca chuoi thanh 1 dang duy nhat (dung cho che do khop chinh xac).
export const normalizeShortAnswer = (raw, caseSensitive = false) => (
    finalizeAnswerToken(trimUnbalancedBraces(preNormalizeAnswer(raw)), caseSensitive)
);

// Tach cau tra loi thanh danh sach nghiem da chuan hoa, khong trung lap.
export const tokenizeShortAnswer = (raw, caseSensitive = false) => {
    const tokens = splitAnswerParts(preNormalizeAnswer(raw))
        .map((part) => finalizeAnswerToken(part, caseSensitive))
        .filter(Boolean)
        .flatMap(expandPlusMinus);
    return Array.from(new Set(tokens));
};

/**
 * Cham cau tra loi ngan theo thuat toan so sanh + trong so.
 *
 * @param {string} userAnswer - Nguoi dung nhap (Unicode/ASCII/LaTeX deu duoc).
 * @param {string[]} acceptedAnswers - Cac dap an chap nhan (moi phan tu la 1 phuong an).
 * @param {{caseSensitive?: boolean, exactMatch?: boolean}} config - textInputConfig cua cau hoi.
 * @param {object} options - Ghi de DEFAULT_SCORING_OPTIONS, them:
 *   matchMode: 'set' (mac dinh, tap nghiem du va khong thua) | 'contains' (chua dap an la dung).
 * @returns {{
 *   isCorrect: boolean,
 *   score: number,          // 0..1, dung de hien "muc do khop" hoac cham diem tung phan
 *   matchedAnswer: ?string, // phuong an chap nhan khop nhat
 *   matches: {user: string, correct: string, score: number}[],
 *   missing: string[],      // nghiem dung chua co trong cau tra loi
 *   extra: string[],        // nghiem nguoi dung ghi thua / sai
 * }}
 */
export const scoreShortAnswer = (userAnswer, acceptedAnswers, config = {}, options = {}) => {
    const settings = {
        ...DEFAULT_SCORING_OPTIONS,
        matchMode: 'set',
        ...options,
        weights: { ...DEFAULT_SCORING_OPTIONS.weights, ...(options.weights || {}) },
    };
    let best = { isCorrect: false, score: 0, matchedAnswer: null, matches: [], missing: [], extra: [] };

    if (!replaceAll(String(userAnswer ?? ''), ANSWER_PLACEHOLDER, '').trim()) {
        return best;
    }

    const answers = Array.isArray(acceptedAnswers) ? acceptedAnswers : [acceptedAnswers];
    answers.forEach((acceptedAnswer) => {
        if (!String(acceptedAnswer ?? '').trim()) {
            return;
        }
        const result = scoreAgainstAnswer(userAnswer, acceptedAnswer, config, settings);
        // Uu tien phuong an duoc chap nhan, sau do toi phuong an diem cao hon.
        const isBetter = result.isCorrect !== best.isCorrect ? result.isCorrect : result.score > best.score;
        if (isBetter) {
            best = { ...result, matchedAnswer: acceptedAnswer };
        }
    });

    return best;
};

// Chi can biet dung/sai (cac man lam bai dang dung).
export const isShortAnswerCorrect = (userAnswer, acceptedAnswers, config = {}, options = {}) => (
    scoreShortAnswer(userAnswer, acceptedAnswers, config, options).isCorrect
);

// ============================================================
// XEM TRƯỚC CÔNG THỨC NGƯỜI DÙNG ĐANG GÕ
// "2√3" -> "$2\sqrt{3}$", "(1)/(2)" -> "$\frac{1}{2}$", "[1 2; 3 4]" -> bmatrix
// ============================================================
const PREVIEW_SYMBOLS = {
    'π': '\\pi ', '*': '\\cdot ', '×': '\\times ', '·': '\\cdot ', '÷': '\\div ', '±': '\\pm ', '∓': '\\mp ',
    '≤': '\\le ', '≥': '\\ge ', '≠': '\\ne ', '≈': '\\approx ', '≡': '\\equiv ', '∞': '\\infty ',
    '°': '^{\\circ}', '℃': '{}^{\\circ}\\text{C}', '′': "'", '″': "''",
    '<': '\\lt ', '>': '\\gt ', '{': '\\{', '}': '\\}', '%': '\\%', '#': '\\#', '&': '\\&',
    '~': '\\sim ', '\\': '\\backslash ', '$': '', '‖': '\\|',
    '∈': '\\in ', '∉': '\\notin ', '⊂': '\\subset ', '⊄': '\\not\\subset ', '⊆': '\\subseteq ', '⊃': '\\supset ',
    '∪': '\\cup ', '∩': '\\cap ', '∖': '\\setminus ', '∅': '\\varnothing ',
    'ℝ': '\\mathbb{R}', 'ℕ': '\\mathbb{N}', 'ℤ': '\\mathbb{Z}', 'ℚ': '\\mathbb{Q}',
    '∀': '\\forall ', '∃': '\\exists ', '→': '\\to ', '↔': '\\leftrightarrow ', '⇒': '\\Rightarrow ',
    '⇔': '\\Leftrightarrow ', '⇌': '\\rightleftharpoons ', '↑': '\\uparrow ', '↓': '\\downarrow ',
    '∠': '\\angle ', '△': '\\triangle ', '⊥': '\\perp ', '∥': '\\parallel ', '≅': '\\cong ',
    '∽': '\\backsim ', '⌒': '\\frown ',
    '∫': '\\int ', '∬': '\\iint ', '∮': '\\oint ', '∑': '\\sum ', '∏': '\\prod ', '∂': '\\partial ',
    [ANSWER_PLACEHOLDER]: '\\square ',
};
const PREVIEW_LETTER_REGEX = /[a-zA-ZÀ-ɏḀ-ỿ]/;
const PREVIEW_ATOM_REGEX = /^(?:-?\d+(?:[.,]\d+)?|[a-zA-Zα-ωΑ-Ωπ∞□])$/;
const PREVIEW_OPERAND_REGEX = /^(?:-?\d+(?:[.,]\d+)?|[a-zA-Zα-ωΑ-Ωπ∞□])/;
const MATH_NOTATION_REGEX = /[√∛∜π^_±∓≤≥≠≈≡∞°′″□∫∬∮∑∏∂→↔⇒⇔⇌↑↓∈∉⊂⊄⊆⊃∪∩∖∅ℝℕℤℚ∀∃∠△⊥∥≅∽⌒×÷℃α-ωΑ-Ω¹²³⁰⁴-⁹⁺⁻₀-₉]|sqrt|cbrt|\\[a-z]|(?:^|[^a-z])pi(?:[^a-z]|$)|\d\s*\/\s*\d|\[[^\]]*;/i;

export const hasMathNotation = (value) => MATH_NOTATION_REGEX.test(String(value ?? ''));

function readPreviewOperand(text, index) {
    if (text[index] === '(') {
        const close = findClosingBracket(text, index);
        const end = close === -1 ? text.length : close;
        return { latex: convertPlainToLatex(text.slice(index + 1, end)), end: close === -1 ? text.length : close + 1 };
    }
    const match = PREVIEW_OPERAND_REGEX.exec(text.slice(index));
    if (match) {
        return { latex: convertPlainToLatex(match[0]), end: index + match[0].length };
    }
    return { latex: '', end: index };
}

// "[1 2; 3 4]" -> bmatrix. "[1; 3]" (khoang) giu nguyen.
function buildPreviewMatrix(inner) {
    const rows = splitTopLevel(inner, ';').map((row) => row.trim().split(/\s*,\s+|\s+/).filter(Boolean));
    const isMatrix = rows.length >= 3 || rows.some((cells) => cells.length >= 2);
    if (!isMatrix) {
        return '';
    }
    const body = rows.map((cells) => cells.map(convertPlainToLatex).join(' & ')).join(' \\\\ ');
    return `\\begin{bmatrix}${body}\\end{bmatrix}`;
}

function convertPlainToLatex(text) {
    let output = '';
    let index = 0;

    while (index < text.length) {
        const ch = text[index];

        if (ch === '(') {
            const close = findClosingBracket(text, index);
            if (close !== -1) {
                const inner = text.slice(index + 1, close);
                // (a)/(b) -> phan so dung.
                if (text[close + 1] === '/' && text[close + 2] === '(') {
                    const denominatorClose = findClosingBracket(text, close + 2);
                    if (denominatorClose !== -1) {
                        output += `\\frac{${convertPlainToLatex(inner)}}{${convertPlainToLatex(text.slice(close + 3, denominatorClose))}}`;
                        index = denominatorClose + 1;
                        continue;
                    }
                }
                // (x)^2 -> x^2: bo ngoac quanh co so don.
                if (text[close + 1] === '^' && PREVIEW_ATOM_REGEX.test(inner.trim())) {
                    output += convertPlainToLatex(inner.trim());
                    index = close + 1;
                    continue;
                }
            }
            output += '(';
            index += 1;
            continue;
        }

        if (ch === '[') {
            const close = findClosingBracket(text, index, '[', ']');
            const matrix = close === -1 ? '' : buildPreviewMatrix(text.slice(index + 1, close));
            if (matrix) {
                output += matrix;
                index = close + 1;
                continue;
            }
            output += '[';
            index += 1;
            continue;
        }

        if (RADICAL_CHARS.includes(ch)) {
            let cursor = skipSpaces(text, index + 1);
            let degree = ch === '∛' ? '3' : ch === '∜' ? '4' : '';
            if (ch === '√' && text[cursor] === '[') {
                const close = findClosingBracket(text, cursor, '[', ']');
                if (close !== -1) {
                    degree = convertPlainToLatex(text.slice(cursor + 1, close));
                    cursor = close + 1;
                }
            }
            const operand = readPreviewOperand(text, cursor);
            output += `\\sqrt${degree ? `[${degree}]` : ''}{${operand.latex}}`;
            index = operand.end;
            continue;
        }

        if (ch === '^' || ch === '_') {
            const operand = readPreviewOperand(text, skipSpaces(text, index + 1));
            output += `${ch}{${operand.latex}}`;
            index = operand.end;
            continue;
        }

        if (PREVIEW_LETTER_REGEX.test(ch)) {
            let end = index;
            while (end < text.length && PREVIEW_LETTER_REGEX.test(text[end])) {
                end += 1;
            }
            const word = text.slice(index, end);
            index = end;

            if (LATEX_FUNCTIONS.has(word.toLowerCase())) {
                output += `\\${word.toLowerCase()} `;
            } else if (/^[A-Z][a-z]$/.test(word)) {
                // Ky hieu nguyen to (Fe, Cu, Na) viet dung nhu sach hoa.
                output += `\\mathrm{${word}}`;
            } else if (/^[a-zA-Z]{1,2}$/.test(word)) {
                // Bien: x, dx, ab.
                output += word;
            } else {
                output += `\\text{${word}}`;
            }
            continue;
        }

        if (ch === ' ') {
            output += '\\ ';
        } else if (ch === ',' && isDigit(text[index - 1] || '') && isDigit(text[index + 1] || '')) {
            output += '{,}';
        } else {
            output += Object.prototype.hasOwnProperty.call(PREVIEW_SYMBOLS, ch) ? PREVIEW_SYMBOLS[ch] : ch;
        }
        index += 1;
    }

    return output;
}

// Tra ve chuoi "$...$" de dua vao MathText.
export const buildAnswerPreviewText = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) {
        return '';
    }

    // Nguoi dung tu go LaTeX thi giu nguyen.
    if (/\\[a-zA-Z]/.test(raw)) {
        return raw.includes('$') ? raw : `$${raw}$`;
    }

    const prepared = replaceSubscripts(replaceSuperscripts(raw))
        .replace(/\bsqrt\s*(?=[(\d])/gi, '√')
        .replace(/\bcbrt\s*(?=[(\d])/gi, '∛')
        .replace(STANDALONE_PI_REGEX, '$1π');

    return `$${convertPlainToLatex(prepared)}$`;
};
